import {
  Alert as MuiAlert,
  alertClasses as muiAlertClasses,
  AlertTitle as MuiAlertTitle,
  css,
  styled,
} from '@mui/material';
import { useTranslation } from '@kc-console/core';
import {
  IcCloseLargeRegular,
  IcErrorRhombusRegular,
  IcInformationCircleRegular,
  IcSuccessCircleRegular,
  IcWarningTriangleRegular,
} from '@kep/kcds-icons';

import { useElementIsEllipsis } from '../../utils';
import { IconButtonGhost, iconButtonGhostClasses } from '../IconButtonGhost';
import { Tooltip } from '../Tooltip';
import BannerButton from './BannerButton';
import { bannerClasses } from './theme';

import type { BannerCategory, BannerOwnerState, BannerProps } from './types';

const SEVERITY_ICON_MAP = {
  default: IcInformationCircleRegular,
  info: IcInformationCircleRegular,
  error: IcErrorRhombusRegular,
  warning: IcWarningTriangleRegular,
  success: IcSuccessCircleRegular,
} as const satisfies Record<BannerCategory, React.ComponentType>;

const Banner = (props: BannerProps) => {
  const { t } = useTranslation();

  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 props 구조분해에서 처리
  const {
    title,
    children,
    icon,
    buttonProps,
    onClose,
    category = 'default',
    hierarchy = 'low',
    ref,
    ...others
  } = props;

  const ownerState: BannerOwnerState = {
    ...props,
    category,
    hierarchy,
  };

  // title과 description의 말줄임 감지
  const titleOverflow = useElementIsEllipsis();
  const descriptionOverflow = useElementIsEllipsis();

  const Icon = SEVERITY_ICON_MAP[category];
  const titleElement = title && (
    <BannerTitle ownerState={ownerState} ref={titleOverflow.ref}>
      {title}
    </BannerTitle>
  );

  const descriptionElement = (
    <BannerDescription ownerState={ownerState} ref={descriptionOverflow.ref}>
      {children}
    </BannerDescription>
  );
  return (
    <BannerRoot
      ownerState={ownerState}
      ref={ref}
      icon={icon ? icon : <Icon fontSize={24} />}
      action={
        <BannerAction ownerState={ownerState}>
          {buttonProps && (
            <BannerButton buttonProps={buttonProps} color={category} hierarchy={hierarchy} />
          )}
          {onClose && (
            <IconButtonGhost
              onClick={onClose}
              size="sm"
              aria-label={t('common:common.action.close')}
            >
              <IcCloseLargeRegular fontSize={16} />
            </IconButtonGhost>
          )}
        </BannerAction>
      }
      {...others}
    >
      {titleElement && titleOverflow.isEllipsis ? (
        <Tooltip message={titleOverflow.isEllipsis ? title : ''} placement="top">
          {titleElement}
        </Tooltip>
      ) : (
        titleElement
      )}
      {descriptionOverflow.isEllipsis ? (
        <Tooltip message={descriptionOverflow.isEllipsis ? children : ''} placement="top">
          {descriptionElement}
        </Tooltip>
      ) : (
        descriptionElement
      )}
    </BannerRoot>
  );
};

Banner.displayName = 'Banner';

export default Banner;

// Styled Components - theme.ts에서 이전된 스타일
const BannerRoot = styled(MuiAlert, {
  target: bannerClasses.root,
})<{ ownerState: BannerOwnerState }>(({ ownerState, theme }) => {
  const { category, hierarchy } = ownerState;

  return css`
    /* theme.ts에서 이전된 기본 스타일 */
    display: inline-flex;
    overflow: hidden;
    align-items: center;
    width: 100%;
    height: 80px;
    border-radius: 1px;
    padding: 16px;
    gap: 16px;

    & .${muiAlertClasses.icon} {
      margin-right: 0;
    }

    & .${muiAlertClasses.action} {
      margin-right: 0;
    }

    /* category/hierarchy에 따른 배경색 및 아이콘 색상 */
    ${getCategoryHierarchyStyles(category, hierarchy, theme)}
  `;
});

const BannerTitle = styled(MuiAlertTitle, {
  target: bannerClasses.title,
})<{ ownerState: BannerOwnerState }>(({ theme }) => {
  return css`
    ${theme.typography['heading/semibold/lg']};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${theme.color['text/default']};
    margin-bottom: 4px;
  `;
});

const BannerDescription = styled('span', {
  target: bannerClasses.description,
})<{ ownerState: BannerOwnerState }>(({ theme }) => {
  return css`
    ${theme.typography['body/normal/md']};
    display: block;
    text-overflow: ellipsis;
    overflow-x: hidden;
    white-space: nowrap;
    color: ${theme.color['text/weak/default']};
  `;
});

const BannerAction = styled('div', {
  target: bannerClasses.action,
})<{ ownerState: BannerOwnerState }>(({ ownerState, theme }) => {
  const { category, hierarchy } = ownerState;

  return css`
    /* theme.ts에서 이전된 기본 스타일 */
    display: flex;
    align-items: center;
    gap: 16px;

    & .${iconButtonGhostClasses.root} {
      color: ${theme.color['text/default']};
      & svg {
        color: inherit;
      }
    }

    /* hierarchy가 high인 경우 IconButton 색상 변경 (warning 제외) */
    ${hierarchy === 'high' &&
    category !== 'warning' &&
    css`
      & .${iconButtonGhostClasses.root} {
        color: ${theme.color['text/inverse/default']};
        & svg {
          color: inherit;
        }
      }
    `}
  `;
});

// Helper 함수: category와 hierarchy 조합에 따른 스타일 반환
function getCategoryHierarchyStyles(
  category: BannerOwnerState['category'],
  hierarchy: BannerOwnerState['hierarchy'],
  theme: any,
) {
  // default/low
  if (category === 'default' && hierarchy === 'low') {
    return css`
      background-color: ${theme.color['surface/neutral/weakest/default']};
      & svg {
        color: ${theme.color['surface/neutral/strong/default']};
      }
    `;
  }

  // default/high
  if (category === 'default' && hierarchy === 'high') {
    return css`
      background-color: ${theme.color['surface/neutral/strong/default']};
      & svg {
        color: ${theme.color['icon/inverse/default']};
      }
      & .${bannerClasses.title} {
        color: ${theme.color['text/inverse/default']};
      }
      & .${bannerClasses.description} {
        color: ${theme.color['text/inverse/weak/default']};
      }
    `;
  }

  // info/low
  if (category === 'info' && hierarchy === 'low') {
    return css`
      background-color: ${theme.color['surface/informative/weakest/default']};
      & svg {
        color: ${theme.color['icon/informative/default']};
      }
    `;
  }

  // info/high
  if (category === 'info' && hierarchy === 'high') {
    return css`
      background-color: ${theme.color['surface/informative/default']};
      & svg {
        color: ${theme.color['icon/inverse/default']};
      }
      & .${bannerClasses.title} {
        color: ${theme.color['text/inverse/default']};
      }
      & .${bannerClasses.description} {
        color: ${theme.color['text/inverse/weak/default']};
      }
    `;
  }

  // error/low
  if (category === 'error' && hierarchy === 'low') {
    return css`
      background-color: ${theme.color['surface/danger/weakest/default']};
      & svg {
        color: ${theme.color['icon/danger/default']};
      }
    `;
  }

  // error/high
  if (category === 'error' && hierarchy === 'high') {
    return css`
      background-color: ${theme.color['surface/danger/transparent/default']};
      & svg {
        color: ${theme.color['icon/inverse/default']};
      }
      & .${bannerClasses.title} {
        color: ${theme.color['text/inverse/default']};
      }
      & .${bannerClasses.description} {
        color: ${theme.color['text/inverse/weak/default']};
      }
    `;
  }

  // warning/low
  if (category === 'warning' && hierarchy === 'low') {
    return css`
      background-color: ${theme.color['surface/warning/weakest/default']};
      & svg {
        color: ${theme.color['icon/warning/default']};
      }
    `;
  }

  // warning/high
  if (category === 'warning' && hierarchy === 'high') {
    return css`
      background-color: ${theme.color['surface/accent/default']};
      & svg {
        color: ${theme.color['icon/default']};
      }
    `;
  }

  // success/low
  if (category === 'success' && hierarchy === 'low') {
    return css`
      background-color: ${theme.color['surface/success/weakest/default']};
      & svg {
        color: ${theme.color['icon/success/default']};
      }
    `;
  }

  // success/high
  if (category === 'success' && hierarchy === 'high') {
    return css`
      background-color: ${theme.color['surface/success/transparent/default']};
      & svg {
        color: ${theme.color['icon/inverse/default']};
      }
      & .${bannerClasses.title} {
        color: ${theme.color['text/inverse/default']};
      }
      & .${bannerClasses.description} {
        color: ${theme.color['text/inverse/weak/default']};
      }
    `;
  }

  return css``;
}
